import {Component, ChangeDetectionStrategy} from "@angular/core";
import {NzMessageService} from "ng-zorro-antd/message";
import {NzTableQueryParams, NzTableSortOrder} from "ng-zorro-antd/table";
import {lastValueFrom} from "rxjs";
import {
    PagingSearchDto,
    PerFormaAPIService,
    DocumentTypeSearchResultDto,
    DocumentTypeManageDto
} from "src/app/services/pfapi.service";

export interface SortDto {
    key: string;
    value: NzTableSortOrder;
}

@Component({
    selector: "app-document-type",
    templateUrl: "./document-type.component.html",
    styleUrl: "./document-type.component.scss",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DocumentTypeComponent {
    constructor(
        private readonly api: PerFormaAPIService,
        private message: NzMessageService
    ) {}

    documentTypes: DocumentTypeSearchResultDto = {
        Data: [],
        Total: 0
    } as DocumentTypeSearchResultDto;

    loading = true;
    total = 0;
    pageSizeOptions = [10, 15, 20, 50];
    pageSize = 15;
    pageIndex = 1;

    private lastParams: NzTableQueryParams | null = null;

    // start out with a default sort order so
    // that the column header shows the correct state
    defaultSort: SortDto[] = [{key: "", value: "ascend"}];
    sort: SortDto[] = [...this.defaultSort];

    searchField = "DocumentTypeName";
    searchValue = "";
    searchFields = [
        {
            label: "Name",
            value: "DocumentTypeName"
        },
        {
            label: "File Type",
            value: "FileTypeName"
        }
    ];

    columns = [
        {
            title: "Name",
            compare: (a: DocumentTypeManageDto, b: DocumentTypeManageDto) =>
                a.DocumentTypeName.localeCompare(b.DocumentTypeName),
            fieldName: "DocumentTypeName",
            enabled: true
        },
        {
            title: "File Type",
            compare: (a: DocumentTypeManageDto, b: DocumentTypeManageDto) =>
                a.FileTypeName.localeCompare(b.FileTypeName),
            fieldName: "FileTypeName",
            enabled: true
        },
        {
            title: "Documents",
            compare: (a: DocumentTypeManageDto, b: DocumentTypeManageDto) => a.Documents - b.Documents,
            fieldName: "Documents",
            enabled: true
        },
        {
            title: "Aliases",
            compare: (a: DocumentTypeManageDto, b: DocumentTypeManageDto) => a.Aliases - b.Aliases,
            fieldName: "Aliases",
            enabled: true
        },
        {
            title: "Keywords",
            compare: (a: DocumentTypeManageDto, b: DocumentTypeManageDto) => a.Keywords - b.Keywords,
            fieldName: "Keywords",
            enabled: true
        },
        {
            title: "Keyword Aliases",
            compare: (a: DocumentTypeManageDto, b: DocumentTypeManageDto) => a.KeywordAliases - b.KeywordAliases,
            fieldName: "KeywordAliases",
            enabled: true
        },
        {
            title: "Edit",
            compare: null,
            fieldName: null,
            enabled: true
        }
    ];

    GetData = async () => {
        const params: PagingSearchDto = {
            PageIndex: this.pageIndex,
            PageSize: this.pageSize,
            SearchField: this.searchField || null,
            SearchValue: this.searchValue || null
        };

        const sort = this.sort.filter(s => s.value !== null) || [...this.defaultSort];

        if (sort.length) {
            params.SortField = sort[0].key;
            params.SortOrder = sort[0].value;
        }
        try {
            this.documentTypes = await lastValueFrom(this.api.postApiConfigureDocumentTypesSearch(params));
            this.total = this.documentTypes.Total;
        } catch (error) {
            this.message.error("Failed to fetch data");
            console.log(error);
        } finally {
            this.loading = false;
        }
    };

    async onQueryParamsChange(params: NzTableQueryParams): Promise<void> {
        if (this.isParamsSame(this.lastParams, params)) {
            return;
        }

        this.lastParams = {...params};

        this.pageIndex = params.pageIndex;
        this.pageSize = params.pageSize;

        // reset the page index if the sort changes
        if (this.SortChanged(params.sort)) {
            this.pageIndex = 1;
        }

        this.sort = [...params.sort];
        await this.GetData();
    }

    // nz-table likes to fire extraneous onQueryParamsChange events
    // so we need to filter out the ones that don't actually change anything
    private isParamsSame(a: NzTableQueryParams | null, b: NzTableQueryParams): boolean {
        if (!a) return false;
        const activeSortA = a.sort.filter(s => s.value !== null);
        const activeSortB = b.sort.filter(s => s.value !== null);
        return (
            a.pageIndex === b.pageIndex &&
            a.pageSize === b.pageSize &&
            activeSortA.length === activeSortB.length &&
            activeSortA.every((s, i) => s.key === activeSortB[i].key && s.value === activeSortB[i].value)
        );
    }

    SortChanged = (sort: SortDto[]): boolean => {
        // Filter out non-active sorts (where value is null)
        const newSort = sort.filter(s => s.value !== null);
        const currentSort = this.sort
            ? this.sort.filter(s => s.value !== null)
            : [...this.defaultSort].filter(s => s.value !== null);

        // If both are empty, no sort change
        if (newSort.length === 0 && currentSort.length === 0) {
            return false;
        }

        // If lengths differ, there's a change (e.g., one has a sort, the other doesn't)
        if (newSort.length !== currentSort.length) {
            return true;
        }

        const newActiveSort = newSort[0];
        const currentActiveSort = currentSort[0];

        return newActiveSort.key !== currentActiveSort.key || newActiveSort.value !== currentActiveSort.value;
    };

    getSortOrder(fieldName: string): NzTableSortOrder {
        if (!fieldName || !this.sort) {
            return null;
        }
        const sortItem = this.sort.find(s => s.key === fieldName);
        return sortItem ? sortItem.value : null;
    }

    async onSearch(): Promise<void> {
        this.pageIndex = 1;
        await this.GetData();
    }

    async clearSearch(): Promise<void> {
        this.searchField = "DocumentTypeName";
        this.searchValue = "";
        this.pageIndex = 1;
        this.sort = [...this.defaultSort];
        await this.GetData();
    }
}
